import axios from "axios";

import { Bars } from "react-loader-spinner";
import { useEffect, useState, useImperativeHandle } from "react";
import { styled } from "/stitches.config.js";
import { HiDownload } from "react-icons/hi";
import useLocalStorage from "/lib/useLocalStorage";

import posthog from "posthog-js";
import { createPopup } from "@typeform/embed";
import "@typeform/embed/build/css/popup.css";

import Image from "next/image";
import { useSession } from "next-auth/react";
import { useS3Upload } from "next-s3-upload";

const IMAGE_WIDTH = 120;

export default function Gallery({ galleryRef, refreshQuota, modelUrl }) {
  const [uploadingRequests, setUploadingRequests] = useState([]);
  useImperativeHandle(galleryRef, () => ({
    uploadV2: async (thumbnail, file, params) => {
      setUploadingRequests((oldValue) => [
        {
          key: new Date().toString(),
          thumbnail,
          file,
          params,
        },
        ...oldValue,
      ]);
    },
  }));

  const [renderRequests, setRenderRequests] = useLocalStorage(
    "__renderRequestsV2",
    []
  );

  function onUploadDone({ thumbnail, file, callId, jobId }) {
    refreshQuota();

    // Remove from uploading requests.
    setUploadingRequests((oldValue) => oldValue.filter((x) => x.file !== file));

    // Add to rendering requests.
    setRenderRequests((oldValue) =>
      [
        {
          key: callId,
          callId,
          jobId,
          thumbnail,
        },
        ...oldValue,
      ].slice(0, 5)
    );
  }

  function onRenderDone({ callId, imageUrl }) {
    setRenderRequests((oldValue) => {
      for (const x of oldValue) {
        if (x.callId === callId) {
          delete x.thumbnail;
          x.imageUrl = imageUrl;
          break;
        }
      }
      return [...oldValue];
    });
  }

  function onError(callId) {
    // Remove from rendering requests.
    setRenderRequests((oldValue) =>
      oldValue.filter((x) => x.callId !== callId)
    );
  }

  const allRequests = [
    ...uploadingRequests,
    ...renderRequests,
    ...(DefaultImages[modelUrl] || []).map((imageUrl) => ({ imageUrl })),
  ].slice(0, 5);
  return (
    <Container>
      {allRequests.map(({ key, ...props }) => (
        <Item
          key={key}
          {...props}
          onUploadDone={onUploadDone}
          onRenderDone={onRenderDone}
          onError={onError}
        />
      ))}
    </Container>
  );
}

function Item(props) {
  const { imageUrl, thumbnail } = props;
  const src = imageUrl || thumbnail;

  const [formSubmitted, setFormSubmitted] = useLocalStorage(
    "form-submitted-prod"
  );
  const { toggle: toggleDownloadForm } = createPopup("t4fY47pk", {
    onSubmit: (event) => {
      setFormSubmitted(true);
      posthog.capture("PhotoDownloadFormSubmitted", {
        formResponseId: event.response_id,
      });
    },
    size: 70,
    autoClose: true,
  });

  const isAuth = useAuth();
  function downloadImage() {
    if (!formSubmitted && !isAuth && !imageUrl.startsWith("/")) {
      toggleDownloadForm();
    } else {
      window.open(imageUrl);
    }
  }

  return (
    <Row
      onClick={imageUrl && downloadImage}
      style={{
        cursor: imageUrl ? "pointer" : "auto",
      }}
    >
      <Image width={IMAGE_WIDTH} height={IMAGE_WIDTH} src={src} alt="" />
      {imageUrl ? (
        <DownloadButton />
      ) : (
        <Overlay>
          <Centered>
            <Bars height={16} width={16} color="white" />
            {props.callId && <RenderingItem {...props} />}
            {props.file && <UploadingItem {...props} />}
          </Centered>
        </Overlay>
      )}
    </Row>
  );
}

let cachedFile = null;
let cachedUrlPromise = null;

function UploadingItem({ thumbnail, file, params, onUploadDone }) {
  const { uploadToS3, files } = useS3Upload();
  useEffect(() => {
    async function uploadIfNeeded(file) {
      if (file !== cachedFile || !cachedUrlPromise) {
        cachedFile = file;
        cachedUrlPromise = uploadToS3(file).then(({ url }) => url);
      }
      return cachedUrlPromise;
    }

    async function job() {
      const url = await uploadIfNeeded(file);
      const response = await axios.post("/api/render", {
        ...params,
        modelFile: url,
      });

      posthog.capture("RenderingRequested", {
        url,
        resolution: params.resolution,
      });
      const { callId, jobId } = response.data;
      onUploadDone({ thumbnail, file, callId, jobId });
    }
    job();
  }, []);

  return <span>Uploading {Math.round(files[0]?.progress) || 0}%</span>;
}

function RenderingItem({ callId, jobId, onRenderDone, onError }) {
  const [renderingProgress, setRenderingProgress] = useState(0);
  useEffect(() => {
    async function autoRetry() {
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 10000));

        try {
          const resp = await axios.get("/api/render", {
            params: { callId, jobId },
          });
          if (resp.status === 200) {
            onRenderDone({ callId, imageUrl: resp.data.imageUrl });
            return;
          }
          if (resp.status === 202) {
            const {
              data: { progress },
            } = resp;
            setRenderingProgress(progress);
            continue;
          }
        } catch (err) {
          const status = err?.response?.status;
          if (status && status !== 504) {
            onError(callId);
            break;
          } else {
            console.warn(err);
          }
        }
      }
    }

    autoRetry();
  }, []);

  return renderingProgress > 0 ? (
    <span>Rendering {Math.round(renderingProgress * 100)}%</span>
  ) : (
    <span>Preparing</span>
  );
}

function DownloadButton() {
  return (
    <div
      style={{
        position: "absolute",
        width: 38,
        height: 30,
        background: "#374151",
        bottom: 6,
        right: 3,
        borderRadius: 4,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <HiDownload color="white" size={16} />
    </div>
  );
}

const Centered = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
});

const Container = styled("div", {
  width: IMAGE_WIDTH,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 24,
});

const Row = styled("div", {
  position: "relative",
  img: {
    borderRadius: 4,
    background: "url(/transparent.jpg)",
    backgroundSize: "300%",
  },
});

const Overlay = styled("div", {
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  position: "absolute",
  background: "rgba(0, 0, 0, 0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: 12,
  color: "white",
});

function useAuth() {
  const { status } = useSession();
  return status === "authenticated";
}

const DefaultImages = {
  "/models/yoyo_dark.glb": [
    "/images/render_preview/yoyo_dark_1.png",
    "/images/render_preview/yoyo_dark_2.png",
    // "/images/render_preview/yoyo_dark_3.png",
    "/images/render_preview/yoyo_dark_4.png",
  ],
  "/models/furniture_eameschair.glb": [
    "/images/render_preview/echair_1.png",
    "/images/render_preview/echair_2.png",
    "/images/render_preview/echair_3.png",
  ],
  "/models/shoe.glb": [
    "/images/render_preview/shoe_1.png",
    "/images/render_preview/shoe_2.png",
    //"/images/render_preview/shoe_3.png",
    "/images/render_preview/shoe_4.png",
  ],
  "/models/coffee.glb": [
    "/images/render_preview/coffee_1.png",
    "/images/render_preview/coffee_2.png",
    //"/images/render_preview/coffee_3.png",
    "/images/render_preview/coffee_4.png",
  ],
  "/models/watch_diesel.glb": [
    "/images/render_preview/diesel_watch_1.png",
    "/images/render_preview/diesel_watch_2.png",
    "/images/render_preview/diesel_watch_3.png",
  ],
  "/models/jewelry_blackpearl_diamond_std.glb": [
    "/images/render_preview/blackpearl_diamond_1.png",
    // "/images/render_preview/blackpearl_diamond_2.png",
    "/images/render_preview/blackpearl_diamond_3.png",
    "/images/render_preview/blackpearl_diamond_4.png",
  ],
};
